// screens/staff/SegreteriaStudentiScreen.tsx
//
// W6 v1 Session 1 — Segreteria student roster.
//
// HARD INVARIANT: this screen renders ZERO pedagogical fields. The API
// payload is the roster shape (id / first_name / age_bracket / exam_date /
// cohort / enrolled_at / paused) — no mastery_score, no Indice, no streak,
// no Brain. The defense-in-depth FE test asserts that even when a polluted
// fixture leaks pedagogical fields into the response, they don't render.

import { useEffect, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTokens } from '../../state/theme';
import { staffApi, type RosterStudent, type Cohort, type InviteCode, type StaffMe } from '../../api/staff';
import type { RecoveryKit } from '../../api/auth';
import { RecoveryKitDisplay } from '../../components/RecoveryKitDisplay';
import { useBrowserNavigationGuard } from '../../hooks/useBrowserNavigationGuard';
import { formatItalianExamDate } from '../../utils/exam-date';

interface NewStudentFormState {
  first_name:  string;
  age_bracket: 'under_18' | 'adult';
  cohort_id:   string;
  exam_date:   string;
}

const EMPTY_FORM: NewStudentFormState = {
  first_name:  '',
  age_bracket: 'adult',
  cohort_id:   '',
  exam_date:   '',
};

export default function SegreteriaStudentiScreen(): JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const [students, setStudents] = useState<RosterStudent[]>([]);
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // W6 v1 Session 2 — fetch role to decide whether rows are clickable into
  // the per-student detail page. Segreteria CANNOT see pedagogical data,
  // so their rows stay click-inert (BE 403s anyway). Instructors + titolare
  // get a chevron and a navigate to /staff/studenti/:studentId.
  const [role, setRole] = useState<StaffMe['role'] | null>(null);
  useEffect(() => {
    let alive = true;
    staffApi.me().then(m => { if (alive) setRole(m.role); }).catch(() => { /* noop */ });
    return () => { alive = false; };
  }, []);
  const canSeePedagogy = role !== null && role !== 'segreteria';
  const canManageRecovery = role === 'titolare' || role === 'segreteria';

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState<NewStudentFormState>(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [createdInviteCode, setCreatedInviteCode] = useState<InviteCode | null>(null);
  const [createdStudentName, setCreatedStudentName] = useState<string | null>(null);
  const [confirmUnenrol, setConfirmUnenrol] = useState<RosterStudent | null>(null);
  const [moveStudent, setMoveStudent] = useState<RosterStudent | null>(null);
  const [moveCohortId, setMoveCohortId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [recoveryStudent, setRecoveryStudent] = useState<RosterStudent | null>(null);
  const [recoveryKit, setRecoveryKit] = useState<RecoveryKit | null>(null);
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const recoveryRequestIdRef = useRef(0);
  useBrowserNavigationGuard(recoveryLoading || recoveryKit !== null);

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const [s, c] = await Promise.all([staffApi.listStudents(), staffApi.listCohorts()]);
      setStudents(s.students);
      setCohorts(c.cohorts);
    } catch (_err) {
      setError('Non è stato possibile caricare gli studenti. Riprovi a breve.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  const filtered = students.filter(s =>
    s.first_name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!createForm.first_name.trim()) return;
    setCreating(true);
    try {
      const res = await staffApi.createStudent({
        first_name:  createForm.first_name.trim(),
        age_bracket: createForm.age_bracket,
        cohort_id:   createForm.cohort_id || null,
        exam_date:   createForm.exam_date ? new Date(createForm.exam_date).toISOString() : null,
      });
      setCreatedStudentName(res.student.first_name);
      // Reset the form but stay in modal — offer the optional invite-code mint
      setCreateForm({ ...EMPTY_FORM, cohort_id: createForm.cohort_id });
      await refresh();
    } catch (_err) {
      setError('Impossibile creare lo studente. Verifichi i dati e riprovi.');
    } finally {
      setCreating(false);
    }
  }

  async function handleMintCode() {
    if (!createForm.cohort_id && cohorts.length > 0) return;  // require cohort if any
    setCreating(true);
    try {
      const cohortId = createForm.cohort_id || (cohorts[0]?.id);
      const res = await staffApi.createInviteCode({
        purpose: 'student',
        cohort_id: cohortId || null,
        max_uses: 1,
        notes: createdStudentName ? `Codice creato per ${createdStudentName}` : null,
      });
      setCreatedInviteCode(res.invite_code);
    } catch (_err) {
      setError('Impossibile creare il codice di invito.');
    } finally {
      setCreating(false);
    }
  }

  function closeCreateModal() {
    setShowCreate(false);
    setCreateForm(EMPTY_FORM);
    setCreatedInviteCode(null);
    setCreatedStudentName(null);
  }

  async function handleUnenrol(s: RosterStudent) {
    setActionLoading(true);
    try {
      await staffApi.updateStudent(s.id, { paused: true });
      await refresh();
    } catch {
      setError('Impossibile disiscrivere lo studente.');
    } finally {
      setActionLoading(false);
      setConfirmUnenrol(null);
    }
  }

  async function handleMoveCohort() {
    if (!moveStudent) return;
    setActionLoading(true);
    try {
      await staffApi.updateStudent(moveStudent.id, { cohort_id: moveCohortId || null });
      await refresh();
    } catch {
      setError('Impossibile spostare lo studente nella classe selezionata.');
    } finally {
      setActionLoading(false);
      setMoveStudent(null);
      setMoveCohortId('');
    }
  }

  function closeRecoveryModal(): void {
    recoveryRequestIdRef.current += 1;
    setRecoveryStudent(null);
    setRecoveryKit(null);
    setRecoveryError(null);
    setRecoveryLoading(false);
  }

  async function handleRecoveryKit(student: RosterStudent): Promise<void> {
    const requestId = recoveryRequestIdRef.current + 1;
    recoveryRequestIdRef.current = requestId;
    setRecoveryStudent(student);
    setRecoveryKit(null);
    setRecoveryError(null);
    setRecoveryLoading(true);
    try {
      const kit = await staffApi.rotateStudentRecoveryKit(student.id);
      if (recoveryRequestIdRef.current !== requestId) return;
      setRecoveryKit(kit);
    } catch {
      if (recoveryRequestIdRef.current !== requestId) return;
      setRecoveryError('Impossibile generare il kit di recupero. Riprovi tra poco.');
    } finally {
      if (recoveryRequestIdRef.current === requestId) setRecoveryLoading(false);
    }
  }

  async function copyInviteToClipboard() {
    if (!createdInviteCode) return;
    try { await navigator.clipboard.writeText(createdInviteCode.code); } catch { /* ignore */ }
  }

  return (
    <section data-testid="segreteria-studenti-screen">
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: tokens.fonts.serif, fontSize: 26, fontWeight: 700, margin: 0 }}>Studenti iscritti</h1>
          <p style={{ color: tokens.textMuted, margin: '4px 0 0 0', fontSize: 13 }}>
            Solo i dati anagrafici e di iscrizione. Per i progressi pedagogici si rivolga agli istruttori.
          </p>
        </div>
        <button
          data-testid="segreteria-new-student"
          onClick={() => setShowCreate(true)}
          style={{
            background:   tokens.accent,
            color:        tokens.mode === 'dark' ? '#1a1510' : '#fff',
            border:       'none',
            borderRadius: tokens.radius.pill,
            padding:      '10px 20px',
            cursor:       'pointer',
            fontSize:     14,
            fontWeight:   600,
          }}
        >
          + Nuovo studente
        </button>
      </header>

      <input
        type="search"
        placeholder="Cerca per nome…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        aria-label="Cerca studente per nome"
        data-testid="segreteria-search"
        style={{
          width:        '100%',
          maxWidth:     400,
          marginBottom: 16,
          background:   tokens.surface,
          border:       `1px solid ${tokens.line}`,
          borderRadius: tokens.radius.md,
          padding:      '10px 14px',
          color:        tokens.text,
          fontSize:     14,
        }}
      />

      {error && (
        <div role="alert" style={{ marginBottom: 16, color: tokens.wrong, fontSize: 13 }}>{error}</div>
      )}

      {loading ? (
        <div data-testid="segreteria-students-loading" style={{ color: tokens.textMuted, fontSize: 14 }}>Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div data-testid="segreteria-students-empty" style={{ color: tokens.textMuted, fontSize: 14, padding: '40px 0', textAlign: 'center' }}>
          {search.trim() ? 'Nessuno studente trovato per questa ricerca.' : 'Non ci sono ancora studenti iscritti.'}
        </div>
      ) : (
        <div role="table" aria-label="Lista studenti" data-testid="segreteria-students-table" style={{ width: '100%', border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.md, overflow: 'hidden' }}>
          <div role="row" style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 0.8fr', padding: '10px 14px', background: tokens.surface, fontSize: 12, color: tokens.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
            <span>Nome</span>
            <span>Età</span>
            <span>Classe</span>
            <span>Esame</span>
            <span>Iscritto il</span>
            <span style={{ textAlign: 'right' }}>Azioni</span>
          </div>
          {filtered.map((s) => (
            <div
              key={s.id}
              role="row"
              data-testid={`segreteria-student-row-${s.id}`}
              onClick={canSeePedagogy && !s.paused ? () => navigate(`/staff/studenti/${s.id}`) : undefined}
              style={{
                display: 'grid',
                gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr 0.8fr',
                padding: '12px 14px',
                borderTop: `1px solid ${tokens.line}`,
                fontSize: 14,
                alignItems: 'center',
                opacity: s.paused ? 0.55 : 1,
                cursor: canSeePedagogy && !s.paused ? 'pointer' : 'default',
              }}
            >
              <span>
                {canSeePedagogy && !s.paused && (
                  <span aria-hidden="true" style={{ color: tokens.textMuted, marginRight: 6 }}>›</span>
                )}
                {s.first_name}{s.paused ? ' (disiscritto)' : ''}
              </span>
              <span style={{ color: tokens.textMuted, fontSize: 13 }}>{s.age_bracket === 'under_18' ? 'Min. 18' : 'Adulto'}</span>
              <span style={{ color: tokens.textMuted, fontSize: 13 }}>{s.cohort_name ?? '—'}</span>
              <span style={{ color: tokens.textMuted, fontSize: 13 }}>{formatItalianExamDate(s.exam_date) ?? '—'}</span>
              <span style={{ color: tokens.textMuted, fontSize: 13 }}>{new Date(s.enrolled_at).toLocaleDateString('it-IT')}</span>
              <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
                {canManageRecovery && (
                  <button
                    onClick={(e) => { e.stopPropagation(); void handleRecoveryKit(s); }}
                    data-testid={`segreteria-recovery-${s.id}`}
                    aria-label={`Nuovo kit di recupero per ${s.first_name}`}
                    disabled={s.paused || recoveryLoading}
                    style={{
                      background: 'transparent', color: tokens.accentInk,
                      border: `1px solid ${tokens.line}`, borderRadius: tokens.radius.sm,
                      padding: '6px 10px', fontSize: 12,
                      cursor: s.paused || recoveryLoading ? 'not-allowed' : 'pointer',
                    }}
                  >Kit</button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setMoveStudent(s); setMoveCohortId(s.cohort_id ?? ''); }}
                  data-testid={`segreteria-move-${s.id}`}
                  aria-label={`Sposta classe per ${s.first_name}`}
                  disabled={s.paused || actionLoading}
                  style={{
                    background:   'transparent',
                    color:        tokens.textMuted,
                    border:       `1px solid ${tokens.line}`,
                    borderRadius: tokens.radius.sm,
                    padding:      '6px 10px',
                    fontSize:     12,
                    cursor:       s.paused || actionLoading ? 'not-allowed' : 'pointer',
                  }}
                >Sposta</button>
                <button
                  onClick={(e) => { e.stopPropagation(); setConfirmUnenrol(s); }}
                  data-testid={`segreteria-unenrol-${s.id}`}
                  aria-label={`Disiscrivi ${s.first_name}`}
                  disabled={s.paused || actionLoading}
                  style={{
                    background:   'transparent',
                    color:        tokens.wrong,
                    border:       `1px solid ${tokens.line}`,
                    borderRadius: tokens.radius.sm,
                    padding:      '6px 10px',
                    fontSize:     12,
                    cursor:       s.paused || actionLoading ? 'not-allowed' : 'pointer',
                  }}
                >Disiscrivi</button>
              </span>
            </div>
          ))}
        </div>
      )}

      {recoveryStudent && (
        <ModalShell
          title="Nuovo kit di recupero"
          onClose={closeRecoveryModal}
          dismissDisabled={recoveryLoading || recoveryKit !== null}
          testid="segreteria-recovery-modal"
        >
          <p style={{ margin: '0 0 12px', color: tokens.text, fontSize: 14 }}>
            Nuovo kit per <strong>{recoveryStudent.first_name}</strong>.
          </p>
          {recoveryLoading && <p role="status" style={{ color: tokens.textMuted, fontSize: 14 }}>Generazione…</p>}
          {recoveryError && (
            <>
              <p role="alert" style={{ color: tokens.wrongText, fontSize: 14 }}>{recoveryError}</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={closeRecoveryModal} style={btnSecondary(tokens)}>Chiudi</button>
              </div>
            </>
          )}
          {recoveryKit && (
            <RecoveryKitDisplay kit={recoveryKit} onDone={closeRecoveryModal} doneLabel="Ho salvato il kit" />
          )}
        </ModalShell>
      )}

      {/* Modal: New student */}
      {showCreate && (
        <ModalShell
          title={createdInviteCode ? 'Codice creato' : (createdStudentName ? 'Studente creato' : 'Nuovo studente')}
          onClose={closeCreateModal}
          testid="segreteria-new-student-modal"
        >
          {createdInviteCode ? (
            <div data-testid="segreteria-invite-code-result">
              <p style={{ marginBottom: 12, color: tokens.textMuted, fontSize: 14 }}>Comunichi questo codice allo studente:</p>
              <div style={{ background: tokens.surface, padding: '14px 18px', borderRadius: tokens.radius.md, fontFamily: tokens.fonts.mono, fontSize: 22, letterSpacing: 1, marginBottom: 16, textAlign: 'center' }}>
                {createdInviteCode.code}
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={copyInviteToClipboard} style={btnSecondary(tokens)}>Copia</button>
                <button onClick={closeCreateModal} style={btnPrimary(tokens)} data-testid="segreteria-new-student-done">Fatto</button>
              </div>
            </div>
          ) : createdStudentName ? (
            <div>
              <p style={{ marginBottom: 16, color: tokens.text, fontSize: 14 }}>
                {createdStudentName} è stato/a iscritto/a. Vuole creare un codice di invito ora, da consegnare allo studente?
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={closeCreateModal} style={btnSecondary(tokens)}>No, in seguito</button>
                <button onClick={handleMintCode} disabled={creating} style={btnPrimary(tokens)} data-testid="segreteria-mint-invite-code">
                  {creating ? 'Generazione…' : 'Crea codice di invito ora'}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <FieldLabel tokens={tokens} label="Nome">
                <input
                  type="text"
                  value={createForm.first_name}
                  onChange={(e) => setCreateForm({ ...createForm, first_name: e.target.value })}
                  required
                  autoFocus
                  style={inputStyle(tokens)}
                  data-testid="segreteria-new-student-name"
                />
              </FieldLabel>
              <FieldLabel tokens={tokens} label="Età">
                <select
                  value={createForm.age_bracket}
                  onChange={(e) => setCreateForm({ ...createForm, age_bracket: e.target.value as 'under_18' | 'adult' })}
                  style={inputStyle(tokens)}
                  data-testid="segreteria-new-student-age"
                >
                  <option value="adult">18 o più</option>
                  <option value="under_18">Meno di 18</option>
                </select>
              </FieldLabel>
              <FieldLabel tokens={tokens} label="Classe (opzionale)">
                <select
                  value={createForm.cohort_id}
                  onChange={(e) => setCreateForm({ ...createForm, cohort_id: e.target.value })}
                  style={inputStyle(tokens)}
                  data-testid="segreteria-new-student-cohort"
                >
                  <option value="">Nessuna classe</option>
                  {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </FieldLabel>
              <FieldLabel tokens={tokens} label="Data esame (opzionale)">
                <input
                  type="date"
                  value={createForm.exam_date}
                  onChange={(e) => setCreateForm({ ...createForm, exam_date: e.target.value })}
                  style={inputStyle(tokens)}
                />
              </FieldLabel>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" onClick={closeCreateModal} style={btnSecondary(tokens)}>Annulla</button>
                <button type="submit" disabled={creating || !createForm.first_name.trim()} style={btnPrimary(tokens)} data-testid="segreteria-new-student-submit">
                  {creating ? 'Salvataggio…' : 'Iscrivi'}
                </button>
              </div>
            </form>
          )}
        </ModalShell>
      )}

      {/* Modal: Unenrol confirm */}
      {confirmUnenrol && (
        <ModalShell title="Conferma disiscrizione" onClose={() => setConfirmUnenrol(null)} testid="segreteria-unenrol-modal">
          <p style={{ marginBottom: 16, fontSize: 14 }}>
            Confermare la disiscrizione di <strong>{confirmUnenrol.first_name}</strong>? Lo studente non potrà più accedere fino a riattivazione.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setConfirmUnenrol(null)} style={btnSecondary(tokens)}>Annulla</button>
            <button onClick={() => handleUnenrol(confirmUnenrol)} disabled={actionLoading} style={btnPrimary(tokens)} data-testid="segreteria-unenrol-confirm">
              {actionLoading ? 'Operazione in corso…' : 'Disiscrivi'}
            </button>
          </div>
        </ModalShell>
      )}

      {/* Modal: Move cohort */}
      {moveStudent && (
        <ModalShell title={`Sposta ${moveStudent.first_name} in un'altra classe`} onClose={() => setMoveStudent(null)} testid="segreteria-move-modal">
          <FieldLabel tokens={tokens} label="Classe di destinazione">
            <select value={moveCohortId} onChange={(e) => setMoveCohortId(e.target.value)} style={inputStyle(tokens)} data-testid="segreteria-move-cohort-select">
              <option value="">Nessuna classe</option>
              {cohorts.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FieldLabel>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
            <button onClick={() => setMoveStudent(null)} style={btnSecondary(tokens)}>Annulla</button>
            <button onClick={handleMoveCohort} disabled={actionLoading} style={btnPrimary(tokens)} data-testid="segreteria-move-confirm">
              {actionLoading ? 'Operazione in corso…' : 'Sposta'}
            </button>
          </div>
        </ModalShell>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

function inputStyle(tokens: ReturnType<typeof useTokens>): React.CSSProperties {
  return {
    background:   tokens.surface,
    border:       `1px solid ${tokens.line}`,
    borderRadius: tokens.radius.md,
    padding:      '10px 14px',
    color:        tokens.text,
    fontSize:     14,
    width:        '100%',
    boxSizing:    'border-box',
    fontFamily:   tokens.fonts.body,
    outline:      'none',
  };
}

function btnPrimary(tokens: ReturnType<typeof useTokens>): React.CSSProperties {
  return {
    background:   tokens.accent,
    color:        tokens.mode === 'dark' ? '#1a1510' : '#fff',
    border:       'none',
    borderRadius: tokens.radius.pill,
    padding:      '10px 18px',
    cursor:       'pointer',
    fontSize:     14,
    fontWeight:   600,
  };
}

function btnSecondary(tokens: ReturnType<typeof useTokens>): React.CSSProperties {
  return {
    background:   'transparent',
    color:        tokens.textMuted,
    border:       `1px solid ${tokens.line}`,
    borderRadius: tokens.radius.pill,
    padding:      '10px 18px',
    cursor:       'pointer',
    fontSize:     14,
  };
}

function FieldLabel({ tokens, label, children }: { tokens: ReturnType<typeof useTokens>; label: string; children: React.ReactNode }): JSX.Element {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: tokens.textMuted }}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function ModalShell({
  title,
  onClose,
  children,
  testid,
  dismissDisabled = false,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  testid?: string;
  dismissDisabled?: boolean;
}): JSX.Element {
  const tokens = useTokens();
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Capture the element that had focus before the modal opened so we can
    // restore it on close (a11y: keyboard users keep their place in the page).
    previouslyFocusedRef.current = (document.activeElement as HTMLElement) ?? null;
    const node = containerRef.current;
    if (node) {
      const first = node.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      (first ?? node).focus();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Tab' && dismissDisabled) {
        e.preventDefault();
        const node = containerRef.current;
        if (!node) return;
        const focusable = Array.from(node.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ));
        if (focusable.length === 0) {
          node.focus();
          return;
        }
        const currentIndex = focusable.indexOf(document.activeElement as HTMLElement);
        const nextIndex = e.shiftKey
          ? (currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1)
          : (currentIndex < 0 || currentIndex === focusable.length - 1 ? 0 : currentIndex + 1);
        focusable[nextIndex]?.focus();
        return;
      }
      if (e.key === 'Escape' && !dismissDisabled) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previouslyFocusedRef.current?.focus?.();
    };
  }, [dismissDisabled, onClose]);

  return (
    <div
      data-testid={testid}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !dismissDisabled) onClose(); }}
    >
      <div ref={containerRef} tabIndex={-1} style={{ background: tokens.bg, borderRadius: tokens.radius.lg, padding: 24, minWidth: 420, maxWidth: 520, border: `1px solid ${tokens.line}`, outline: 'none' }}>
        <h2 style={{ fontFamily: tokens.fonts.serif, fontSize: 20, fontWeight: 700, margin: '0 0 16px 0' }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}
